// Search Identity Engine — Pass 3.1
// ---------------------------------------------------------------------------
// ONE deterministic source of truth for every page's search identity.
// Given a URL path (+ optional entity/CMS data) it returns the canonical
// { title, description, canonical, ogTitle, ogUrl, robots } for that page, and
// it validates any served identity against the Pass 2 invariants I1–I7.
//
// Pure ES module — no Vite/import.meta/window/DOM. Runs identically in Node
// (the Validator/auditor, the Release Gate) and in the browser (the render
// path, once wired). Spec: Architecture/Organic_Search_Experience_Pass2_Design.md
// ---------------------------------------------------------------------------

import {
  getCountryBySlug,
  getRegionBySlug,
  getCityBySlug,
} from '../data/geo.js';
import { CATEGORY_SLUGS, getCategoryBySlug } from '../data/categoryRegistry.js';

export const DEFAULT_SITE_URL = 'https://luxuryweddingdirectory.com';

// The generic shell / global-fallback titles a NON-homepage page must never carry (I4).
// Un-prerendered routes fall back to dist/index.html (the prerendered homepage), so the
// current homepage title IS the shell-served title — it must be listed here so a non-home
// page wearing it is flagged, while the homepage itself is exempt (isHomepage, below).
// When the homepage title changes, update the first entry. The bare brand is the raw
// index.html sentinel; the 'Curated …' lines are retired homepage titles kept as guards.
const FALLBACK_TITLES = new Set([
  'Luxury Wedding Directory | Wedding Venues, Planners & Vendors', // current homepage = shell-served
  'Luxury Wedding Directory',                                       // raw index.html shell sentinel
  'Luxury Wedding Directory | Curated Venues, Planners & Vendors',  // retired
  'Luxury Wedding Directory, Curated Venues, Planners & Vendors',   // retired
]);

// Single-segment static routes that speak for themselves (not the homepage).
// value = deterministic title for that page.
const STATIC_TITLES = {
  'about-us':        'About Luxury Wedding Directory',
  'vision':          'Our Vision | Luxury Wedding Directory',
  'contact-us':      'Contact | Luxury Wedding Directory',
  'contact':         'Contact | Luxury Wedding Directory',
  'faq':             'Wedding Planning FAQ | Luxury Wedding Directory',
  'careers':         'Careers | Luxury Wedding Directory',
  'press':           'Press | Luxury Wedding Directory',
  'pricing':         'Pricing | Luxury Wedding Directory',
  'all-destinations':'All Wedding Destinations | Luxury Wedding Directory',
  'publications':    'The Magazine | Luxury Wedding Directory',
  'magazine':        'The Magazine | Luxury Wedding Directory',
  'community':       'Community | Luxury Wedding Directory',
  'events':          'Wedding Events | Luxury Wedding Directory',
  // Authority pages (Search-Critical) — added for Search Rendering Layer Wave 1.
  'the-lwd-standard':   'The LWD Standard | Luxury Wedding Directory',
  'editorial-standards':'Editorial Standards | Luxury Wedding Directory',
  'artistry-awards':    'The Artistry Awards | Luxury Wedding Directory',
  // Wedding Atlas index — the hub for the /{region}-wedding-guide destination guides.
  'wedding-guides':     'The Wedding Atlas | Luxury Wedding Directory',
};

const TITLE_MAX = 60;   // I5 — distinctive clause must survive the SERP cut
const DESC_MAX  = 160;

// ── helpers ────────────────────────────────────────────────────────────────

export function normalizePath(input) {
  let p = String(input || '/');
  try { if (/^https?:\/\//i.test(p)) p = new URL(p).pathname; } catch { /* keep */ }
  p = p.split('#')[0].split('?')[0];
  if (p.length > 1) p = p.replace(/\/+$/, '');       // strip trailing slash
  if (!p.startsWith('/')) p = '/' + p;
  return p || '/';
}

function segs(path) {
  const p = normalizePath(path);
  return p === '/' ? [] : p.slice(1).split('/');
}

/** Collapse consecutive duplicated path segments (/a/b/b/c → /a/b/c). Used for canonical
 *  consolidation of malformed URLs where a segment repeats its neighbour. */
export function dedupeConsecutiveSegments(path) {
  const s = segs(path);
  const out = [];
  for (const seg of s) if (out[out.length - 1] !== seg) out.push(seg);
  return out.length ? '/' + out.join('/') : '/';
}

/** I5 title fit: keep the core clause; append the brand suffix only if it fits;
 *  never emit a title over TITLE_MAX unless the core itself already is. */
export function fitTitle(core, brand = 'Luxury Wedding Directory', max = TITLE_MAX) {
  core = String(core || '').trim();
  const withBrand = brand ? `${core} | ${brand}` : core;
  if (withBrand.length <= max) return withBrand;
  if (core.length <= max) return core;                // drop brand first (I5)
  return core.slice(0, max - 1).replace(/\s+\S*$/, '').trim() + '…';
}

// Directory + geographic HUB titles (Search Title strategy, Search_Architecture.md):
// lead with the real search phrase, and ALWAYS carry the full brand. Unlike fitTitle, the
// brand is NEVER dropped to stay under 60 — consistency beats saving characters, and
// over-length is a WARN (I5), never a release failure. No automatic "Luxury" prefix, no
// "Worldwide". "Best"/"Top" are reserved for editorially-curated collections (their titles
// come from the author's headline) and are NEVER fabricated by the engine.
const BRAND = 'Luxury Wedding Directory';
function brandTitle(core) {
  return `${String(core || '').trim()} | ${BRAND}`;
}

/** A CMS/editorial title wins ONLY if it survives the invariants once normalised
 *  (verbose brand stripped, "| LWD" applied, ≤60 with no truncation). A non-compliant
 *  editorial title yields to the clean generated one — length discipline (I5) has no
 *  exception for being hand-written. Returns provenance for the override log:
 *  { title, source: 'engine'|'cms'|'cms-normalised'|'cms-rejected', rule } */
export function pickTitle(cmsTitle, generated, brand = 'LWD') {
  if (!cmsTitle) return { title: generated, source: 'engine', rule: null };
  const fit = fitTitle(stripBrand(cmsTitle), brand);
  if (fit.includes('…') || fit.length > 62) {
    return { title: generated, source: 'cms-rejected', rule: 'I5_LENGTH' };
  }
  return { title: fit, source: fit === cmsTitle ? 'cms' : 'cms-normalised', rule: null };
}

function clampDesc(s, max = DESC_MAX) {
  s = String(s || '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  return s.slice(0, max - 1).replace(/\s+\S*$/, '').trim() + '…';
}

const isCategory = (s) => CATEGORY_SLUGS.includes(s);

// ── classification (ports the identity-relevant parts of pathToState) ───────

export function classify(path) {
  const s = segs(path);
  const out = { path: normalizePath(path), pageType: 'unknown', slugs: {} };
  if (s.length === 0) return { ...out, pageType: 'homepage' };

  // editorial
  if (s[0] === 'lwd-edit' || s[0] === 'the-edit') {
    if (s.length === 1) return { ...out, pageType: 'editorial-hub' };
    if (s[1] === 'category') return { ...out, pageType: 'editorial-category', slugs: { category: s[2] } };
    return { ...out, pageType: 'editorial-article', slugs: { slug: s[1] } };
  }
  // showcase
  if (s[0] === 'showcase' && s.length === 2) return { ...out, pageType: 'showcase', slugs: { slug: s[1] } };
  // publications
  if (s[0] === 'publications') {
    if (s.length === 1) return { ...out, pageType: 'static', slugs: { staticKey: 'publications' } };
    return { ...out, pageType: 'publication', slugs: { slug: s[1] } };
  }
  // planners / suppliers
  if (s[0] === 'wedding-planners' && s.length === 2) return { ...out, pageType: 'supplier-planner', slugs: { slug: s[1] } };
  if (s.length === 3 && s[1] === 'wedding-planners') return { ...out, pageType: 'supplier-planner', slugs: { country: s[0], slug: s[2] } };
  // venue reviews — canonical /{country}/{region}/{category}/{slug}/reviews; /venues/{slug}/reviews
  // is a deprecated legacy alias, kept classifiable so it still resolves an identity if ever hit.
  if (s.length === 5 && s[4] === 'reviews' && isCategory(s[2])) {
    return { ...out, pageType: 'venue-reviews', slugs: { country: s[0], region: s[1], category: s[2], slug: s[3] } };
  }
  if (s.length === 3 && s[0] === 'venues' && s[2] === 'reviews') {
    return { ...out, pageType: 'venue-reviews', slugs: { slug: s[1] } };
  }
  // static single-segment
  if (s.length === 1 && STATIC_TITLES[s[0]]) return { ...out, pageType: 'static', slugs: { staticKey: s[0] } };

  // Region guides — /{region}-wedding-guide (the Wedding Atlas; `region_guides` rows).
  // MUST precede the single-segment geographic fallback below, which would otherwise
  // classify /calabria-wedding-guide as a COUNTRY named "Calabria-Wedding-Guide" and
  // mint a wrong-but-plausible identity. The guide's region is carried separately
  // (region_guides.region_slug) and is NOT derivable by trimming the suffix in every
  // case, so `slug` is the route key and `region` is only a display hint.
  if (s.length === 1 && s[0].endsWith('-wedding-guide')) {
    return { ...out, pageType: 'region-guide', slugs: { slug: s[0], region: s[0].replace(/-wedding-guide$/, '') } };
  }

  // category-bearing routes
  const catIdx = s.findIndex(isCategory);
  if (catIdx !== -1) {
    if (s.length === 1) return { ...out, pageType: 'region-category', slugs: { category: s[0] } };
    if (s.length === 2 && catIdx === 1) return { ...out, pageType: 'region-category', slugs: { country: s[0], category: s[1] } };
    if (s.length === 3 && catIdx === 2) return { ...out, pageType: 'region-category', slugs: { country: s[0], region: s[1], category: s[2] } };
    if (s.length === 4 && catIdx === 2) return { ...out, pageType: 'venue-listing', slugs: { country: s[0], region: s[1], category: s[2], slug: s[3] } };
    if (s.length === 4 && catIdx === 3) return { ...out, pageType: 'city-category', slugs: { country: s[0], region: s[1], city: s[2], category: s[3] } };
    if (s.length === 2 && catIdx === 0) return { ...out, pageType: 'venue-listing', slugs: { category: s[0], slug: s[1] } };
  }

  // geographic hubs
  if (s.length === 1) return { ...out, pageType: 'country', slugs: { country: s[0] } };
  if (s.length === 2) return { ...out, pageType: 'region', slugs: { country: s[0], region: s[1] } };
  if (s.length === 3) return { ...out, pageType: 'city', slugs: { country: s[0], region: s[1], city: s[2] } };

  return out; // unknown
}

// ── name resolution (geo + category → human labels) ─────────────────────────

function names(slugs) {
  // Track any display name that had to be derived from the slug (no catalogue match).
  // A slug can never restore diacritics ("kitzbhel" → "Kitzbhel", not "Kitzbühel"), so
  // an unresolved name must FAIL validation rather than silently ship (I7 / accuracy).
  const unresolved = [];
  const resolve = (slug, lookup, key) => {
    if (!slug) return '';
    const found = lookup(slug);
    const name = found?.name || found?.label;
    // Reject sentinel/alias returns: getRegionBySlug() returns an "All Regions"
    // sentinel (slug 'all') for ANY unknown slug — a false-positive that would ship a
    // misleading, duplicated title. Any lookup that returns a different slug than asked
    // is not a real match → treat as unresolved (distinct slug-derived name + FLAG).
    const isSentinel = found?.slug && slug !== 'all' && found.slug !== slug;
    if (name && !isSentinel) return name;
    unresolved.push(key);
    return titleCaseSlug(slug);
  };
  return {
    country:  resolve(slugs.country,  getCountryBySlug,  'country'),
    region:   resolve(slugs.region,   getRegionBySlug,   'region'),
    city:     resolve(slugs.city,     getCityBySlug,     'city'),
    category: resolve(slugs.category, getCategoryBySlug, 'category'),
    slug:     slugs.slug,
    unresolved,
  };
}

function titleCaseSlug(slug) {
  if (!slug) return '';
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ── the resolver: ideal identity per page type (Pass 2 Ch.1 + Ch.3) ─────────

export function resolveIdentity(path, opts = {}) {
  const siteUrl = (opts.siteUrl || DEFAULT_SITE_URL).replace(/\/$/, '');
  const cms = opts.cms || {};                         // seo_title/seo_description/etc.
  const entity = opts.entity || {};                   // { name, city, ... } for listing/showcase/etc.
  const { pageType, slugs: _rawSlugs, path: norm } = classify(path);
  // A region slug identical to its country (e.g. /italy/italy/…) is a malformed route —
  // treat it as country-level rather than mint an invalid region identity.
  const slugs = (_rawSlugs.region && _rawSlugs.region === _rawSlugs.country)
    ? { ..._rawSlugs, region: undefined } : _rawSlugs;
  const n = names(slugs);
  // Canonical consolidation: a malformed URL with a consecutive duplicated segment
  // (/cyprus/paphos/paphos/…, /italy/rome/rome/…) canonicalises to its deduplicated form,
  // so the alternates point at ONE preferred URL instead of each claiming to be canonical.
  const cleanPath = dedupeConsecutiveSegments(norm);
  const canonical = siteUrl + cleanPath;              // I2 — self, or the consolidated parent

  let title = '', description = '', robots = 'index, follow';
  let titleSource = 'engine', titleRule = null;
  const applyCms = (generated) => { const p = pickTitle(cms.seo_title, generated); title = p.title; titleSource = p.source; titleRule = p.rule; };

  switch (pageType) {
    case 'homepage':
      title = 'Luxury Wedding Directory | Wedding Venues, Planners & Vendors';
      description = "The curated authority for luxury and destination weddings — verified venues, planners and vendors worldwide.";
      break;
    // Geographic HUBS lead with "Weddings"; CATEGORY pages lead with "Luxury {Category}".
    // Title reflects PAGE TYPE (a city hub vs a city venue-results page differ by rule, not by
    // human edit). Place-name ladder (country → region,country → city,country) keeps siblings
    // distinct (I3). Names come from the geo catalogue, which preserves diacritics.
    // Geographic HUBS are ENGINE-OWNED titles (Search Title Strategy): the formula is the
    // single source, so a stale stored/CMS seo_title (often the retired "Luxury Wedding
    // Venues in {region}" form) never overrides it. CMS seo_description is still honoured.
    case 'country':
      title = brandTitle(`Weddings in ${n.country}`);
      description = cms.seo_description ? clampDesc(cms.seo_description) : `Curated luxury wedding venues, planners and inspiration across ${n.country} — verified by LWD.`;
      break;
    case 'region':
      title = brandTitle(`Weddings in ${n.region}, ${n.country}`);
      description = cms.seo_description ? clampDesc(cms.seo_description) : `The luxury wedding authority for ${n.region}, ${n.country} — curated venues, cities and editorial for your celebration.`;
      break;
    case 'city':
      title = brandTitle(`Weddings in ${n.city}, ${n.country}`);
      description = cms.seo_description ? clampDesc(cms.seo_description) : `Where to marry in ${n.city}, ${n.country} — curated luxury wedding venues in and around ${n.city}.`;
      break;
    // Wedding Atlas guide — editorial destination knowledge (when to marry, where to
    // stay, costs, logistics). DELIBERATELY distinct from the region hub it may share a
    // place with: /italy/tuscany is a directory of curated listings, /tuscany-wedding-guide
    // is the written guide to the destination. Same place, different job — differentiate,
    // never canonical one to the other (the Listing-vs-Showcase rule).
    // The place name comes from the guide row (entity.name), NOT the geo catalogue: 116 of
    // the 124 published guides cover regions the catalogue does not carry, and a slug can
    // never restore diacritics. Falling back to the slug is a last resort.
    case 'region-guide': {
      const place = entity.name || titleCaseSlug(slugs.region || '');
      applyCms(brandTitle(`${place} Wedding Guide`));
      description = clampDesc(
        cms.seo_description ||
        entity.description ||
        `A complete guide to weddings in ${place} — when to marry, where to celebrate, what it costs.`,
      );
      break;
    }
    case 'city-category': {
      const cat = String(n.category);
      // Venue/vendor RESULTS page for a city → "{Category} in {City}, {Country}" (a directory
      // index — no superlative; distinct from the city HUB, which leads with "Weddings").
      // Engine-owned (like region-category) — no stale CMS override.
      title = brandTitle(`${n.category} in ${n.city}, ${n.country}`);
      description = cms.seo_description ? clampDesc(cms.seo_description) : `Curated ${cat.toLowerCase()} for luxury weddings in ${n.city}, ${n.country}.`;
      break;
    }
    case 'region-category': {
      const cat = String(n.category);
      if (!n.country && !n.region) {           // global category hub
        title = brandTitle(`${n.category}`);
        description = `Explore the world's finest ${cat.toLowerCase()}, a curated global collection by LWD.`;
      } else if (n.region) {                   // region-level category
        title = brandTitle(`${n.category} in ${n.region}, ${n.country}`);
        description = `A curated selection of ${cat.toLowerCase()} for luxury weddings in ${n.region}, ${n.country}.`;
      } else {                                 // country-level category
        title = brandTitle(`${n.category} in ${n.country}`);
        description = `A curated selection of ${cat.toLowerCase()} for luxury weddings across ${n.country}.`;
      }
      break;
    }
    case 'venue-listing':
      title = fitTitle(cms.seo_title ? stripBrand(cms.seo_title) : `${entity.name || titleCaseSlug(slugs.slug)}${n.city ? `, ${n.city}` : ''}`, 'Luxury Wedding Venue');
      description = clampDesc(cms.seo_description || entity.description || `${entity.name || titleCaseSlug(slugs.slug)} — a curated luxury wedding venue${n.region ? ` in ${n.region}` : ''}.`);
      break;
    case 'venue-reviews': {
      const nm = entity.name || titleCaseSlug(slugs.slug);
      title = fitTitle(`Reviews`, `${nm}${n.region ? `, ${n.region}` : ''}`);
      description = clampDesc(
        cms.seo_description ||
        (entity.reviewCount
          ? `${entity.reviewCount} verified review${entity.reviewCount === 1 ? '' : 's'} from real couples who married at ${nm}${n.region ? `, ${n.region}` : ''}.`
          : `Verified reviews from real couples who married at ${nm}${n.region ? `, ${n.region}` : ''}.`)
      );
      break;
    }
    case 'showcase': {
      // A Showcase is LWD's PRESENTATION of a venue — its title MUST stay distinct from the
      // venue LISTING (Pass 2: Listing ≠ Showcase; their titles must not converge). CMS
      // seo_title is frequently copied from the listing, so we deliberately generate the
      // distinct "{name} Weddings | {location}" form rather than trusting the CMS title.
      const nm = entity.name || titleCaseSlug(slugs.slug);
      title = fitTitle(`${nm} Weddings`, entity.location || 'Luxury Wedding Directory');
      description = clampDesc(cms.seo_description || entity.description || `An immersive look at weddings at ${nm}, curated by LWD.`);
      break;
    }
    case 'publication':
      title = fitTitle(cms.seo_title ? stripBrand(cms.seo_title) : (entity.title || titleCaseSlug(slugs.slug)), 'The LWD Magazine');
      description = clampDesc(cms.seo_description || entity.intro || `Read ${entity.title || titleCaseSlug(slugs.slug)}, an edition of the LWD Magazine.`);
      break;
    case 'editorial-article': {
      // The headline IS the identity and must match the H1 — never truncate it.
      // Append "| LWD" only when it fits; a long headline stays whole (length WARN, but
      // consistent), rather than being chopped mid-word.
      const head = (cms.seo_title ? stripBrand(cms.seo_title) : (entity.title || titleCaseSlug(slugs.slug))).trim();
      title = (`${head} | LWD`).length <= 60 ? `${head} | LWD` : head;
      description = clampDesc(cms.seo_description || entity.standfirst || entity.excerpt || `${entity.title || titleCaseSlug(slugs.slug)} — from The LWD Edit.`);
      break;
    }
    case 'editorial-hub':
      title = fitTitle('The LWD Edit', 'Luxury Wedding Journal');
      description = 'The LWD Edit — considered writing on luxury and destination weddings: places, people and design.';
      break;
    case 'editorial-category':
      title = fitTitle(`${titleCaseSlug(slugs.category)} | The LWD Edit`, '');
      description = clampDesc(`Editorial on ${titleCaseSlug(slugs.category)} from The LWD Edit.`);
      break;
    case 'supplier-planner':
      title = fitTitle(cms.seo_title ? stripBrand(cms.seo_title) : `${entity.name || titleCaseSlug(slugs.slug)}`, entity.discipline || 'Wedding Planner');
      description = clampDesc(cms.seo_description || entity.description || `${entity.name || titleCaseSlug(slugs.slug)} — a verified luxury wedding planner recognised by LWD.`);
      break;
    case 'static':
      title = STATIC_TITLES[slugs.staticKey] || fitTitle(titleCaseSlug(slugs.staticKey), 'Luxury Wedding Directory');
      description = clampDesc(cms.seo_description || `${titleCaseSlug(slugs.staticKey)} — Luxury Wedding Directory.`);
      break;
    default:
      title = ''; description = ''; robots = 'noindex, follow';
  }

  return {
    pageType, path: norm, canonical,
    title, description,
    ogTitle: title, ogUrl: canonical, ogDescription: description,
    robots,
    // provenance for the override log + validator
    titleSource, titleRule,
    cmsTitle: cms.seo_title || null,
    unresolvedNames: n.unresolved || [],
  };
}

function stripBrand(t) {
  return String(t || '').replace(/\s*[|·,]\s*(Luxury Wedding Directory|LWD)( ·.*)?$/i, '').trim();
}

// ── the validator: served identity vs invariants I1–I7 ──────────────────────
// served = { title, description, canonical } scraped from a page.
// context = { corpus: Map<title,count>, parentTitle } for cross-page checks.

export function validateIdentity(served, path, opts = {}) {
  const siteUrl = (opts.siteUrl || DEFAULT_SITE_URL).replace(/\/$/, '');
  const ideal = resolveIdentity(path, opts);
  const norm = normalizePath(path);
  const ownCanonical = siteUrl + norm;
  const t = String(served?.title || '').trim();
  const desc = String(served?.description || '').trim();
  const can = String(served?.canonical || '').trim().replace(/\/$/, '') || '';

  const isHomepage = ideal.pageType === 'homepage';
  const checks = [];
  const add = (id, name, ok, detail) => checks.push({ id, name, ok, detail });

  // I4 — no inherited / fallback identity (also the I1 raw-HTML symptom).
  // The homepage legitimately carries the brand title; only NON-homepage pages
  // fail for wearing it. Any page with no title fails.
  const isFallback = !t || (!isHomepage && FALLBACK_TITLES.has(t));
  add('I4', 'No inherited/fallback identity', !isFallback,
    isFallback ? `served title is the shell/fallback: "${t || '(empty)'}"` : '');

  // I2 — explicit self-canonical equal to own path (trailing slash insignificant;
  // root accepts with or without the slash).
  const canonicalPresent = !!can;
  const canonicalSelf = can.replace(/\/$/, '') === ownCanonical.replace(/\/$/, '');
  add('I2', 'Explicit self-canonical', canonicalPresent && canonicalSelf,
    !canonicalPresent ? 'no canonical served'
      : !canonicalSelf ? `canonical points elsewhere: ${can} (expected ${ownCanonical})` : '');

  // I5 — length discipline
  const lenOk = t.length > 0 && t.length <= 62;
  add('I5', 'Title length ≤ 60', lenOk, lenOk ? '' : `title is ${t.length} chars`);

  // I3 — sibling-distinctness (needs corpus of SELF-CANONICAL titles). A shared title is
  // only a blocker when this page is self-canonical; a page that canonicalises elsewhere
  // (consolidated to a preferred URL) defers to its canonical and is not a duplicate.
  // The homepage owns the brand title (copiers fail I4), so it is exempt.
  if (opts.corpus && !isHomepage && canonicalSelf) {
    const dupCount = opts.corpus.get(t) || 0;
    const distinct = !t || dupCount <= 1;
    add('I3', 'Title distinct from self-canonical siblings', distinct,
      distinct ? '' : `title shared by ${dupCount} self-canonical pages`);
  }

  // A display name resolved only from the slug (no catalogue match) cannot be trusted —
  // FAIL so it enters the exceptions queue rather than silently shipping an invalid
  // geographic identity. Two distinct classes:
  //   • UNKNOWN_REGION       — region not in the catalogue (the "All Regions" sentinel case)
  //   • DISPLAY_NAME_FROM_SLUG — city/country name derived from slug (e.g. lost diacritics)
  // (Category slugs are a fixed registry, so category unresolution is not a hard fail.)
  const hardUnresolved = (ideal.unresolvedNames || []).filter(k => k !== 'category');
  const unknownRegion = hardUnresolved.includes('region');
  const slugNames = hardUnresolved.filter(k => k !== 'region');   // city/country
  // UNKNOWN_REGION is a hard FAIL — an uncatalogued region mints a fake geographic identity
  // (the "All Regions" sentinel). A city/country name derived from a slug is a WARN: it is
  // usually correct (budapest→Budapest) and only occasionally wrong (kitzbhel→Kitzbhel), which
  // is unknowable without the catalogue — so flag for review, don't block. Genuinely broken
  // ones are fixed by adding them to the catalogue (see supplementalCities.js).
  add('NAME_REGION', 'Region resolved from catalogue', !unknownRegion,
    unknownRegion ? 'UNKNOWN_REGION: region not in geo catalogue' : '');
  add('NAME_SLUG', 'Display name from catalogue (not slug)', slugNames.length === 0,
    slugNames.length ? `DISPLAY_NAME_FROM_SLUG: ${slugNames.join(', ')} — review; add to catalogue if wrong` : '');

  // description presence
  add('DESC', 'Description present', !!desc, desc ? '' : 'no meta description served');

  // verdict — hard fails: fallback identity, wrong canonical, duplicate title, unknown region.
  // warns: length, missing desc, slug-derived name (usually fine, review not block).
  const fails = checks.filter(c => !c.ok && ['I4', 'I2', 'I3', 'NAME_REGION'].includes(c.id));
  const warns = checks.filter(c => !c.ok && ['I5', 'DESC', 'NAME_SLUG'].includes(c.id));
  const verdict = fails.length ? 'FAIL' : warns.length ? 'WARN' : 'PASS';

  // CMS override log entry (Tai's safeguard): editorial intent stays visible.
  const override = (ideal.titleSource && ideal.titleSource !== 'engine') ? {
    url: norm, cmsTitle: ideal.cmsTitle, finalTitle: ideal.title,
    source: ideal.titleSource, rule: ideal.titleRule,
  } : null;

  return {
    path: norm, pageType: ideal.pageType, verdict,
    served: { title: t, description: desc, canonical: can },
    ideal: { title: ideal.title, description: ideal.description, canonical: ideal.canonical },
    checks, override,
    reasons: [...fails, ...warns].map(c => `${c.id}: ${c.detail}`),
  };
}

export default { classify, resolveIdentity, validateIdentity, normalizePath, fitTitle, DEFAULT_SITE_URL };
